import { useState, useEffect, useCallback } from 'react';
import { 
  Box, Tabs, Tab, Typography, Paper, TextField, Button, 
  IconButton, Grid, Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Alert, CircularProgress, Divider, List, ListItem, ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  PointOfSale as POSIcon,
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { settingsService } from '../../services/api';
import EconomicActivitySelect from '../../components/common/EconomicActivitySelect';
import ValidatedTextField from '../../components/common/ValidatedTextField';
import { validateNIT, validateNRC, validateDUI, validateEmail, validatePhone, validateCodigoEstablecimiento, validateCodigoPuntoVenta, validateCodigoDepartamento, validateCodigoMunicipio, ValidationMessages } from '../../components/common/ValidationPatterns';

// Helper para obtener la empresa actual del localStorage
const getCurrentCompany = () => {
  try {
    const modules = localStorage.getItem('nx2026_modules');
    if (modules) {
      const parsed = JSON.parse(modules);
      if (parsed && parsed.length > 0) {
        return parsed[0].company;
      }
    }
  } catch (e) {
    console.error('Error getting company:', e);
  }
  return null;
};

const SettingsPage = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [catalogs, setCatalogs] = useState(null);
  const [branches, setBranches] = useState([]);
  const [posList, setPOSList] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  
  // Estados para diálogos
  const [branchDialog, setBranchDialog] = useState({ open: false, mode: 'create', data: {} });
  const [posDialog, setPOSDialog] = useState({ open: false, mode: 'create', data: {} });
  const [logoDialog, setLogoDialog] = useState(false);

  // Cargar companyId una sola vez al montar
  useEffect(() => {
    const comp = getCurrentCompany();
    if (comp && comp.id) {
      setCompanyId(comp.id);
    }
  }, []);

  // Función para cargar datos
  const loadData = useCallback(async () => {
    if (!companyId) return;
    
    setLoading(true);
    setError(null);
    try {
      // Cargar catálogos
      const catalogsRes = await settingsService.getCatalogs();
      if (catalogsRes.success) {
        setCatalogs(catalogsRes.data);
      }
      
      // Cargar empresa
      const companyRes = await settingsService.getCompany(companyId);
      if (companyRes.success) {
        setCompany(companyRes.data);
      }
      
      // Cargar sucursales
      const branchesRes = await settingsService.getBranches(companyId);
      if (branchesRes.success) {
        setBranches(branchesRes.data);
      }
      
      // Cargar puntos de venta
      const posRes = await settingsService.getPOS(companyId);
      if (posRes.success) {
        setPOSList(posRes.data);
      }
    } catch (err) {
      setError('Error al cargar los datos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId, loadData]);

  const handleCompanyChange = (field, value) => {
    setCompany(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveCompany = async () => {
    if (!companyId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await settingsService.updateCompany(companyId, company);
      if (res.success) {
        setSuccess('Empresa actualizada correctamente');
        setCompany(res.data);
      } else {
        setError(res.error || 'Error al actualizar');
      }
    } catch (err) {
      setError('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Handlers para Logo
  const handleLogoUpload = async (event) => {
    if (!companyId) return;
    const file = event.target.files[0];
    if (!file) return;
    
    setSaving(true);
    setError(null);
    try {
      // Enviar el archivo directamente como FormData
      const res = await settingsService.uploadLogo(companyId, file);
      if (res.success) {
        setSuccess('Logo actualizado correctamente');
        loadData();
        setLogoDialog(false);
      } else {
        setError(res.error || 'Error al subir logo');
      }
    } catch (err) {
      setError('Error al subir logo');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Handlers para Sucursales
  const handleSaveBranch = async () => {
    if (!companyId) return;
    setSaving(true);
    setError(null);
    try {
      const data = branchDialog.data;
      let res;
      if (branchDialog.mode === 'create') {
        res = await settingsService.createBranch(companyId, data);
      } else {
        res = await settingsService.updateBranch(data.id, data);
      }
      if (res.success) {
        setSuccess('Sucursal guardada correctamente');
        loadData();
        setBranchDialog({ open: false, mode: 'create', data: {} });
      } else {
        setError(res.error || 'Error al guardar');
      }
    } catch (err) {
      setError('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm('¿Está seguro de eliminar esta sucursal?')) return;
    setSaving(true);
    try {
      const res = await settingsService.deleteBranch(branchId);
      if (res.success) {
        setSuccess('Sucursal eliminada');
        loadData();
      } else {
        setError(res.error);
      }
    } catch (err) {
      setError('Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  // Handlers para POS
  const handleSavePOS = async () => {
    if (!companyId) return;
    setSaving(true);
    setError(null);
    try {
      const data = posDialog.data;
      let res;
      if (posDialog.mode === 'create') {
        res = await settingsService.createPOS(companyId, data);
      } else {
        res = await settingsService.updatePOS(data.id, data);
      }
      if (res.success) {
        setSuccess('Punto de venta guardado correctamente');
        loadData();
        setPOSDialog({ open: false, mode: 'create', data: {} });
      } else {
        setError(res.error || 'Error al guardar');
      }
    } catch (err) {
      setError('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePOS = async (posId) => {
    if (!window.confirm('¿Está seguro de eliminar este punto de venta?')) return;
    setSaving(true);
    try {
      const res = await settingsService.deletePOS(posId);
      if (res.success) {
        setSuccess('Punto de venta eliminado');
        loadData();
      } else {
        setError(res.error);
      }
    } catch (err) {
      setError('Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!companyId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No hay empresa seleccionada</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', width: '100%' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Configuración
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tab} 
          onChange={(e, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<BusinessIcon />} label="Empresa" iconPosition="start" />
          <Tab icon={<StoreIcon />} label="Sucursales" iconPosition="start" />
          <Tab icon={<POSIcon />} label="Cajas/POS" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* TAB 1: EMPRESA */}
      {tab === 0 && company && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Logo Section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Box 
                  sx={{ 
                    width: 150, 
                    height: 150, 
                    border: '2px dashed #ccc',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundImage: company.url_logo ? `url(${company.url_logo})` : 'none',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundSize: 'contain',
                    bgcolor: !company.url_logo ? '#f5f5f5' : 'transparent'
                  }}
                >
                  {!company.url_logo && <BusinessIcon sx={{ fontSize: 60, color: '#ccc' }} />}
                </Box>
                <Button 
                  variant="outlined" 
                  startIcon={<UploadIcon />}
                  onClick={() => setLogoDialog(true)}
                >
                  Cambiar Logo
                </Button>
              </Box>
            </Grid>

            {/* Company Fields */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre Legal"
                    value={company.legal_name || ''}
                    onChange={(e) => handleCompanyChange('legal_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre Comercial"
                    value={company.commercial_name || ''}
                    onChange={(e) => handleCompanyChange('commercial_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ValidatedTextField
                    fullWidth
                    label="NIT"
                    value={company.nit || ''}
                    onChange={(value) => handleCompanyChange('nit', value)}
                    validator={validateNIT}
                    errorMessage={ValidationMessages.NIT}
                    inputProps={{ maxLength: 17, pattern: '[0-9\-]' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ValidatedTextField
                    fullWidth
                    label="NRC"
                    value={company.nrc || ''}
                    onChange={(value) => handleCompanyChange('nrc', value)}
                    validator={validateNRC}
                    errorMessage={ValidationMessages.NRC}
                    inputProps={{ maxLength: 8, pattern: '[0-9]' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ValidatedTextField
                    fullWidth
                    label="DUI"
                    value={company.dui || ''}
                    onChange={(value) => handleCompanyChange('dui', value)}
                    validator={validateDUI}
                    errorMessage={ValidationMessages.DUI}
                    inputProps={{ maxLength: 10, pattern: '[0-9\-]' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Empresa</InputLabel>
                    <Select
                      value={company.company_type || 'juridic'}
                      label="Tipo de Empresa"
                      onChange={(e) => handleCompanyChange('company_type', e.target.value)}
                    >
                      {catalogs?.companyTypes?.map(t => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tamaño Contribuyente</InputLabel>
                    <Select
                      value={company.taxpayer_size || 'small'}
                      label="Tamaño Contribuyente"
                      onChange={(e) => handleCompanyChange('taxpayer_size', e.target.value)}
                    >
                      {catalogs?.taxpayerSizes?.map(t => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Categoría</InputLabel>
                    <Select
                      value={company.company_category || 'comercio'}
                      label="Categoría"
                      onChange={(e) => handleCompanyChange('company_category', e.target.value)}
                    >
                      {catalogs?.companyCategories?.map(t => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    value={company.main_address || ''}
                    onChange={(e) => handleCompanyChange('main_address', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Departamento</InputLabel>
                    <Select
                      value={company.main_departamento_code || ''}
                      label="Departamento"
                      onChange={(e) => handleCompanyChange('main_departamento_code', e.target.value)}
                    >
                      {catalogs?.departamentos?.map(d => (
                        <MenuItem key={d.codigo} value={d.codigo}>{d.nombre}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Municipio</InputLabel>
                    <Select
                      value={company.main_municipio_code || ''}
                      label="Municipio"
                      onChange={(e) => handleCompanyChange('main_municipio_code', e.target.value)}
                    >
                      {catalogs?.municipios
                        ?.filter(m => m.departamento_codigo === company.main_departamento_code)
                        .map(m => (
                          <MenuItem key={m.codigo} value={m.codigo}>{m.nombre}</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ValidatedTextField
                    fullWidth
                    label="Email Facturación"
                    value={company.bill_email || ''}
                    onChange={(value) => handleCompanyChange('bill_email', value)}
                    validator={validateEmail}
                    errorMessage={ValidationMessages.EMAIL}
                    validateOnBlur
                    inputProps={{ maxLength: 150 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ValidatedTextField
                    fullWidth
                    label="Teléfono"
                    value={company.bill_phone || ''}
                    onChange={(value) => handleCompanyChange('bill_phone', value)}
                    validator={validatePhone}
                    errorMessage={ValidationMessages.PHONE}
                    inputProps={{ maxLength: 30, pattern: '[0-9\-\+ ]' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Ambiente</InputLabel>
                    <Select
                      value={company.environment || 'test'}
                      label="Ambiente"
                      onChange={(e) => handleCompanyChange('environment', e.target.value)}
                    >
                      {catalogs?.environments?.map(e => (
                        <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <EconomicActivitySelect
                    label="Código Actividad Económica"
                    value={company.economic_activity_code || ''}
                    onChange={(value) => handleCompanyChange('economic_activity_code', value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Pie de Factura"
                    value={company.invoice_footer_text || ''}
                    onChange={(e) => handleCompanyChange('invoice_footer_text', e.target.value)}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={company.is_retention_agent === 1}
                        onChange={(e) => handleCompanyChange('is_retention_agent', e.target.checked ? 1 : 0)}
                      />
                    }
                    label="Agente de Retención"
                  />
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={company.is_perception_agent === 1}
                        onChange={(e) => handleCompanyChange('is_perception_agent', e.target.checked ? 1 : 0)}
                      />
                    }
                    label="Agente de Percepción"
                    sx={{ ml: 3 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Configuración DTE</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="URL API DTE"
                    value={company.mh_dte_api_url || ''}
                    onChange={(e) => handleCompanyChange('mh_dte_api_url', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="URL API Auth"
                    value={company.mh_auth_api_url || ''}
                    onChange={(e) => handleCompanyChange('mh_auth_api_url', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Usuario DGI"
                    value={company.dgi_user || ''}
                    onChange={(e) => handleCompanyChange('dgi_user', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contraseña DGI"
                    type="password"
                    value={company.dgi_pass || ''}
                    onChange={(e) => handleCompanyChange('dgi_pass', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo Firmador</InputLabel>
                    <Select
                      value={company.firmador_type || 'local'}
                      label="Tipo Firmador"
                      onChange={(e) => handleCompanyChange('firmador_type', e.target.value)}
                    >
                      {catalogs?.firmadorTypes?.map(f => (
                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ruta Certificado"
                    value={company.cert_path || ''}
                    onChange={(e) => handleCompanyChange('cert_path', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button 
                      variant="contained" 
                      startIcon={<SaveIcon />}
                      onClick={handleSaveCompany}
                      disabled={saving}
                    >
                      {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* TAB 2: SUCURSALES */}
      {tab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Sucursales</Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setBranchDialog({ open: true, mode: 'create', data: { is_active: true, is_primary: false } })}
            >
              Nueva Sucursal
            </Button>
          </Box>
          
          {branches.length === 0 ? (
            <Alert severity="info">No hay sucursales configuradas</Alert>
          ) : (
            <Grid container spacing={2}>
              {branches.map(branch => (
                <Grid item xs={12} md={6} key={branch.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">{branch.branch_name}</Typography>
                      <Typography color="textSecondary" variant="body2">
                        Código: {branch.codigo_establecimiento} | Tipo: {branch.tipo_establecimiento}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {branch.address_1}
                      </Typography>
                      <Typography variant="body2">
                        Tel: {branch.phone || 'N/A'} | Email: {branch.email || 'N/A'}
                      </Typography>
                      <FormControlLabel
                        control={<Switch checked={branch.is_primary === 1} disabled />}
                        label="Primary"
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                    <CardActions>
                      <IconButton onClick={() => setBranchDialog({ open: true, mode: 'edit', data: branch })}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteBranch(branch.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* TAB 3: CAJAS/POS */}
      {tab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Puntos de Venta / Cajas</Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setPOSDialog({ 
                open: true, 
                mode: 'create', 
                data: { is_active: true, pos_type: 'pos' } 
              })}
              disabled={branches.length === 0}
            >
              Nueva Caja
            </Button>
          </Box>
          
          {branches.length === 0 ? (
            <Alert severity="warning">Debe crear al menos una sucursal primero</Alert>
          ) : posList.length === 0 ? (
            <Alert severity="info">No hay puntos de venta configurados</Alert>
          ) : (
            <Grid container spacing={2}>
              {posList.map(pos => (
                <Grid item xs={12} md={6} key={pos.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">{pos.pos_name}</Typography>
                      <Typography color="textSecondary" variant="body2">
                        Código: {pos.codigo_punto_venta} | Tipo: {pos.pos_type}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Sucursal: {pos.branch_name} ({pos.branch_code})
                      </Typography>
                      <FormControlLabel
                        control={<Switch checked={pos.is_active === 1} disabled />}
                        label="Activo"
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                    <CardActions>
                      <IconButton onClick={() => setPOSDialog({ open: true, mode: 'edit', data: pos })}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeletePOS(pos.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* Dialogo de Sucursal */}
      <Dialog open={branchDialog.open} onClose={() => setBranchDialog({ open: false, mode: 'create', data: {} })} maxWidth="sm" fullWidth>
        <DialogTitle>{branchDialog.mode === 'create' ? 'Nueva Sucursal' : 'Editar Sucursal'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={8}>
              <ValidatedTextField
                fullWidth
                label="Nombre de Sucursal"
                value={branchDialog.data.branch_name || ''}
                onChange={(value) => setBranchDialog({ 
                  ...branchDialog, 
                  data: { ...branchDialog.data, branch_name: value } 
                })}
                required
                inputProps={{ maxLength: 150 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ValidatedTextField
                fullWidth
                label="Código"
                value={branchDialog.data.codigo_establecimiento || ''}
                onChange={(value) => setBranchDialog({ 
                  ...branchDialog, 
                  data: { ...branchDialog.data, codigo_establecimiento: value } 
                })}
                validator={validateCodigoEstablecimiento}
                errorMessage={ValidationMessages.CODIGO_ESTABLECIMIENTO}
                required
                inputProps={{ maxLength: 4, pattern: '[0-9]' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={branchDialog.data.tipo_establecimiento || '02'}
                  label="Tipo"
                  onChange={(e) => setBranchDialog({ 
                    ...branchDialog, 
                    data: { ...branchDialog.data, tipo_establecimiento: e.target.value } 
                  })}
                >
                  {catalogs?.tiposEstablecimiento?.map(t => (
                    <MenuItem key={t.codigo} value={t.codigo}>{t.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <ValidatedTextField
                fullWidth
                label="Dirección"
                value={branchDialog.data.address_1 || ''}
                onChange={(value) => setBranchDialog({ 
                  ...branchDialog, 
                  data: { ...branchDialog.data, address_1: value } 
                })}
                required
                inputProps={{ maxLength: 500 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Departamento</InputLabel>
                <Select
                  value={branchDialog.data.departamento_code || ''}
                  label="Departamento"
                  onChange={(e) => setBranchDialog({ 
                    ...branchDialog, 
                    data: { ...branchDialog.data, departamento_code: e.target.value } 
                  })}
                >
                  {catalogs?.departamentos?.map(d => (
                    <MenuItem key={d.codigo} value={d.codigo}>{d.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Municipio</InputLabel>
                <Select
                  value={branchDialog.data.municipio_code || ''}
                  label="Municipio"
                  onChange={(e) => setBranchDialog({ 
                    ...branchDialog, 
                    data: { ...branchDialog.data, municipio_code: e.target.value } 
                  })}
                >
                  {catalogs?.municipios
                    ?.filter(m => m.departamento_codigo === branchDialog.data.departamento_code)
                    .map(m => (
                      <MenuItem key={m.codigo} value={m.codigo}>{m.nombre}</MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidatedTextField
                fullWidth
                label="Teléfono"
                value={branchDialog.data.phone || ''}
                onChange={(value) => setBranchDialog({ 
                  ...branchDialog, 
                  data: { ...branchDialog.data, phone: value } 
                })}
                validator={validatePhone}
                errorMessage={ValidationMessages.PHONE}
                inputProps={{ maxLength: 30, pattern: '[0-9\-\+ ]' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidatedTextField
                fullWidth
                label="Email"
                value={branchDialog.data.email || ''}
                onChange={(value) => setBranchDialog({ 
                  ...branchDialog, 
                  data: { ...branchDialog.data, email: value } 
                })}
                validator={validateEmail}
                errorMessage={ValidationMessages.EMAIL}
                inputProps={{ maxLength: 150 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={branchDialog.data.is_primary || false}
                    onChange={(e) => setBranchDialog({ 
                      ...branchDialog, 
                      data: { ...branchDialog.data, is_primary: e.target.checked } 
                    })}
                  />
                }
                label="Sucursal Principal"
              />
              <FormControlLabel
                control={
                  <Switch 
                    checked={branchDialog.data.is_active !== false}
                    onChange={(e) => setBranchDialog({ 
                      ...branchDialog, 
                      data: { ...branchDialog.data, is_active: e.target.checked } 
                    })}
                  />
                }
                label="Activa"
                sx={{ ml: 3 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBranchDialog({ open: false, mode: 'create', data: {} })}>Cancelar</Button>
          <Button onClick={handleSaveBranch} variant="contained" disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo de POS */}
      <Dialog open={posDialog.open} onClose={() => setPOSDialog({ open: false, mode: 'create', data: {} })} maxWidth="sm" fullWidth>
        <DialogTitle>{posDialog.mode === 'create' ? 'Nueva Caja/POS' : 'Editar Caja/POS'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={8}>
              <ValidatedTextField
                fullWidth
                label="Nombre"
                value={posDialog.data.pos_name || ''}
                onChange={(value) => setPOSDialog({ 
                  ...posDialog, 
                  data: { ...posDialog.data, pos_name: value } 
                })}
                required
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ValidatedTextField
                fullWidth
                label="Código"
                value={posDialog.data.codigo_punto_venta || ''}
                onChange={(value) => setPOSDialog({ 
                  ...posDialog, 
                  data: { ...posDialog.data, codigo_punto_venta: value } 
                })}
                validator={validateCodigoPuntoVenta}
                errorMessage={ValidationMessages.CODIGO_PUNTO_VENTA}
                required
                inputProps={{ maxLength: 4, pattern: '[0-9]' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Sucursal</InputLabel>
                <Select
                  value={posDialog.data.branch_id || ''}
                  label="Sucursal"
                  onChange={(e) => setPOSDialog({ 
                    ...posDialog, 
                    data: { ...posDialog.data, branch_id: e.target.value } 
                  })}
                >
                  {branches.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.branch_name} ({b.codigo_establecimiento})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={posDialog.data.pos_type || 'pos'}
                  label="Tipo"
                  onChange={(e) => setPOSDialog({ 
                    ...posDialog, 
                    data: { ...posDialog.data, pos_type: e.target.value } 
                  })}
                >
                  {catalogs?.posTypes?.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={posDialog.data.is_active !== false}
                    onChange={(e) => setPOSDialog({ 
                      ...posDialog, 
                      data: { ...posDialog.data, is_active: e.target.checked } 
                    })}
                  />
                }
                label="Activo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPOSDialog({ open: false, mode: 'create', data: {} })}>Cancelar</Button>
          <Button onClick={handleSavePOS} variant="contained" disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo de Logo */}
      <Dialog open={logoDialog} onClose={() => setLogoDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cambiar Logo</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="logo-upload"
              type="file"
              onChange={handleLogoUpload}
            />
            <label htmlFor="logo-upload">
              <Button 
                variant="contained" 
                component="span" 
                startIcon={<UploadIcon />}
                disabled={saving}
              >
                {saving ? 'Subiendo...' : 'Seleccionar Imagen'}
              </Button>
            </label>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Formatos: PNG, JPG, JPEG. Tamaño recomendado: 200x200px
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
